import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useToast } from '@/components/ui';
import { OrgAdminApiError, transferOrgAssignment, type ScopedAccountRow } from '@/api/orgAdmin';

/**
 * EAA-1820 (frame 85, "Chuyển đơn vị"): moves ONE existing Assignment row to a
 * different đơn vị, in place.
 *
 * THE CONTRACT THIS HOOK ENFORCES, verified against
 * `AssignmentService.transfer_to_organization` (portal-api `services.py`
 * ~L2238-2316) rather than assumed from the design mock:
 *   - `target_level_id` is REQUIRED when the row being moved is level-based
 *     (`row.level_id` set) — a Level belongs to exactly one org, so the operator
 *     MUST pick which Level of the destination org replaces it. The picker is
 *     therefore not optional chrome; submission is blocked client-side until a
 *     target Level is chosen, matching what the server would 400 on anyway.
 *   - `target_level_id` must be OMITTED for a direct-grant row (`row.level_id`
 *     null) — sending one 400s ("must not be set for a direct-grant
 *     assignment"), so the level field never even renders in that case.
 *
 * WHAT THE DESIGN MOCK SHOWS THAT THIS DOES NOT IMPLEMENT. Frame 85 also shows
 * two checkboxes ("Thu hồi quyền theo Level của đơn vị cũ" / "Giữ vai trò gán
 * trực tiếp") — `AssignmentTransferSerializer` has no field for either. The real
 * server behaviour is simpler and unconditional: the ONE assignment row this
 * dialog targets moves to the destination org (and, if level-based, onto the
 * destination Level); it does not touch any OTHER grant the same user_sub might
 * hold elsewhere. Rendering those two checkboxes would promise a choice the API
 * cannot honour, so they are left out rather than wired to nothing.
 */
export function useRowTransfer(invalidate: () => void) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const toast = useToast();

    const [target, setTarget] = useState<ScopedAccountRow | null>(null);
    const [targetOrgId, setTargetOrgId] = useState('');
    const [targetLevelId, setTargetLevelId] = useState('');
    const [error, setError] = useState<string | null>(null);

    const open = useCallback((row: ScopedAccountRow) => {
        setTarget(row);
        setTargetOrgId('');
        setTargetLevelId('');
        setError(null);
    }, []);
    const close = useCallback(() => setTarget(null), []);

    const needsLevel = Boolean(target?.level_id);

    const mutation = useMutation({
        mutationFn: () =>
            transferOrgAssignment(config.apiBase, accessToken as string, target?.organization_id as string, target?.id as string, {
                target_organization_id: targetOrgId,
                ...(needsLevel ? { target_level_id: targetLevelId } : {}),
            }),
        onSuccess: () => {
            setTarget(null);
            invalidate();
            toast.success(
                t('console.hierarchy.scopedAccounts.rowTransfer.success', {
                    identity: target?.user_email || target?.user_sub,
                })
            );
        },
        onError: (err: unknown) => {
            setError(err instanceof OrgAdminApiError ? err.message : t('console.hierarchy.scopedAccounts.rowTransfer.errorFallback'));
        },
    });

    const canSubmit = useMemo(
        () => Boolean(targetOrgId) && (!needsLevel || Boolean(targetLevelId)),
        [targetOrgId, needsLevel, targetLevelId]
    );

    const submit = useCallback(() => {
        if (!canSubmit) return;
        setError(null);
        mutation.mutate();
    }, [canSubmit, mutation]);

    return {
        target,
        open,
        close,
        targetOrgId,
        setTargetOrgId: (next: string) => {
            setTargetOrgId(next);
            setTargetLevelId(''); // EAA-1820: a Level chosen for the OLD org is meaningless in the new one.
        },
        targetLevelId,
        setTargetLevelId,
        needsLevel,
        error,
        submit,
        canSubmit,
        busy: mutation.isPending,
    };
}

export type RowTransferVM = ReturnType<typeof useRowTransfer>;
